import { Fragment, useLayoutEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { BulkActionButton } from "../atoms/BulkActionButton";
import { ConfirmDialog } from "./ConfirmDialog";
import { COLLAPSE_RANK, hiddenKeysForStep, isCompactStep, nextStep } from "./bulkActionBarLayout";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Trash2,
  X,
  GitCompare,
  Share2,
  MessageSquare,
  Download,
  Signature,
  FolderInput,
  TextCursor,
  FilePlus2,
  Layers,
  Loader2,
  MoreHorizontal,
  Eye,
  BadgeCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

interface BulkActionBarProps {
  /**
   * Total selected items (documents + folders). Drives bar visibility and the
   * single-item action layout — folder-inclusive so the bar still appears for a
   * folder-only selection.
   */
  selectedCount: number;
  /**
   * Count shown in the badge — documents only. A folder is never counted as a
   * document. Falls back to selectedCount when omitted (callers without folders).
   */
  documentCount?: number;
  /** Domain-specific label shown beside the count. Defaults to document terminology. */
  selectionLabel?: string;
  /** Handler for clearing selection */
  onClearSelection: () => void;
  /** Handler for deleting selected documents */
  onDelete?: () => void;
  /** Handler for sharing selected documents */
  onShare?: () => void;
  /** Handler for downloading selected documents */
  onDownload?: () => void;
  /** Handler for move/copy operation */
  onMoveCopy?: () => void;
  /** Handler for signature operation */
  onSignature?: () => void;
  /** Handler for asking Franklin about selected documents */
  onAskFranklin?: () => void;
  /** The hosting page's own action on the selection (the Brief page's "Add appendix"). */
  hostAction?: { label: string; icon: LucideIcon; onClick: () => void };
  /** Handler for comparing two PDFs */
  onCompare?: () => void;
  /** Whether compare action is available (exactly 2 PDFs selected) */
  canCompare?: boolean;
  /** Whether IFC compare is available (exactly 2 processed IFCs selected) */
  canCompareModels?: boolean;
  /** Handler for opening the IFC comparison (data-only differences overview) */
  onDiffOverview?: () => void;
  /** Whether "View drawing" is available (exactly 1 parent DWG selected) */
  canViewDrawing?: boolean;
  /** Handler for opening the single selected DWG in the drawing viewer */
  onViewDrawing?: () => void;
  /** Whether "Compare drawings" is available (exactly 2 parent DWGs selected) */
  canCompareDrawings?: boolean;
  /** Handler for opening the two selected DWGs in the drawing compare view */
  onCompareDrawings?: () => void;
  /** Handler for renaming the single selected document (only used when selectedCount === 1) */
  onRename?: () => void;
  /** Handler for uploading a new version of the single selected document (only used when selectedCount === 1) */
  onAddNewVersion?: () => void;
  /** The selected document already has versions: the action reads "Versioning". */
  opensVersioning?: boolean;
  /** Open the comments panel on the single selected document (only used when selectedCount === 1). */
  onComments?: () => void;
  /**
   * Toggle the Verified mark on the whole selection. Undefined hides the
   * action — same capability-gate rule as every other handler here.
   */
  onToggleVerified?: () => void;
  /**
   * Aggregate Verified state of the selection; decides the label. A mixed
   * selection offers "Mark as verified", so one click makes it uniform.
   */
  verifiedState?: "all" | "none" | "mixed";
  /**
   * Extra action buttons rendered as their own group, slotted between the
   * document actions and Delete. Allows non-document callers (e.g. the issues
   * page) to inject domain-specific actions while reusing the same chrome.
   * Receives the bar's `compact` state so the caller can render an icon-only
   * variant when the bar collapses, instead of a full labelled button that
   * would blow out the width the collapse is trying to contain.
   */
  customActions?: (opts: { compact: boolean }) => ReactNode;
  /**
   * True while the selection is still being resolved — e.g. checking a folder
   * cascades into selecting every document inside its subtree. The folder
   * itself shows as selected immediately, but the document count isn't final
   * yet, so the count is replaced with a spinner and every action is disabled
   * until the cascade settles.
   */
  isSelectionPending?: boolean;
}

/**
 * Divider groups, left to right. Each renders after its own vertical rule.
 * The caller's custom actions and the "…" trigger are inserted between
 * `askFranklin` and `delete`; they aren't keyed actions so they aren't listed here.
 */
const GROUP = {
  document: 1,
  context: 2,
  askFranklin: 3,
  delete: 4,
} as const;

/**
 * A single action in the bar. Built once from the handler props, then rendered
 * either on the bar (labelled or icon-only) or as an item in the "…" menu.
 */
interface BulkAction {
  key: string;
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  /** Divider group — see {@link GROUP}. */
  group: number;
  /** Order this action leaves the bar for "…" — see COLLAPSE_RANK. */
  collapseRank: number;
}

/**
 * Run an overflow-menu handler once the menu has finished closing. Radix
 * restores focus to the trigger as it closes; a dialog mounted synchronously
 * from `onSelect` loses focus to that restore, so the handler waits for the
 * next frame. The callback is invoked from a literal arrow — never handed to
 * the timer API directly — so static analysis can see it is not eval-like.
 */
function runAfterMenuCloses(handler: () => void) {
  requestAnimationFrame(() => handler());
}

/**
 * Vertical divider between button groups.
 * Only rendered between groups that have at least one visible action.
 */
function Divider() {
  return <div className="mx-1 h-12 w-px shrink-0 bg-zinc-600" />;
}

/**
 * BulkActionBar - Molecule component
 *
 * Dark floating toolbar for bulk document actions (z-50 — above the chatbox and
 * upload/processing bar, both z-40; modals which portal to body stay above it).
 *
 * Order is fixed: count + Clear | Signature, Share, Download, Move/Copy, Rename |
 * context actions (View drawing, Add new version, Download preview PDF,
 * Compare …) | Ask Franklin | Delete, always last, behind its own divider.
 *
 * Responsive: the bar shows everything the measured width allows. Wide enough →
 * the whole labelled list, no "…" at all. Once it no longer fits, a "…" menu
 * appears and swallows actions one at a time in collapse-rank order (Delete
 * first, Ask Franklin next, context actions, then the rest) while the bar keeps
 * its labels. Only when even that can't fit three labelled actions beside the
 * count does the bar drop to icons (tooltips + aria-labels) and keep folding.
 * Visible actions never shuffle, so an action is either in its place or in "…".
 */
export const BulkActionBar = observer(function BulkActionBar({
  selectedCount,
  documentCount,
  selectionLabel,
  onClearSelection,
  onDelete,
  onShare,
  onDownload,
  onMoveCopy,
  onSignature,
  onAskFranklin,
  hostAction,
  onCompare,
  canCompare = false,
  canCompareModels = false,
  onDiffOverview,
  canViewDrawing = false,
  onViewDrawing,
  canCompareDrawings = false,
  onCompareDrawings,
  onRename,
  onAddNewVersion,
  opensVersioning = false,
  onComments,
  onToggleVerified,
  verifiedState = "none",
  customActions,
  isSelectionPending = false,
}: BulkActionBarProps) {
  const { t } = useLanguage();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Width-aware progressive collapse. `barRef` is the outer pill; its natural
  // width is compared against the viewport and the step is raised until it fits.
  // Always starts at step 0 — the full labelled list — so collapsing is only ever
  // driven by a real measurement, never by a breakpoint guess.
  const barRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef(typeof window === "undefined" ? 0 : window.innerWidth);
  const [step, setStep] = useState(0);

  // Single source of truth for the actions. Order here is the bar order — the
  // same left-to-right order in every mode, and the order of the "…" menu.
  // Built before the collapse effects so the action set can be signature-keyed
  // (see actionSetKey) and the early `selectedCount === 0` return.
  const actions: BulkAction[] = [];

  // Document actions — always the same four (plus Move/Copy where the caller
  // offers it), in the same order.
  if (onSignature) {
    actions.push({
      key: "signature",
      icon: Signature,
      label: t.documents.bulkActions.signature,
      onClick: onSignature,
      group: GROUP.document,
      collapseRank: COLLAPSE_RANK.signature,
    });
  }
  if (onShare) {
    actions.push({
      key: "share",
      icon: Share2,
      label: t.documents.bulkActions.share,
      onClick: onShare,
      group: GROUP.document,
      collapseRank: COLLAPSE_RANK.share,
    });
  }
  if (onDownload) {
    actions.push({
      key: "download",
      icon: Download,
      label: t.documents.bulkActions.download,
      onClick: onDownload,
      group: GROUP.document,
      collapseRank: COLLAPSE_RANK.download,
    });
  }
  if (onMoveCopy) {
    actions.push({
      key: "movecopy",
      icon: FolderInput,
      label: t.documents.bulkActions.moveCopy,
      onClick: onMoveCopy,
      group: GROUP.document,
      collapseRank: COLLAPSE_RANK.movecopy,
    });
  }
  if (onToggleVerified) {
    actions.push({
      key: "verify",
      icon: BadgeCheck,
      label:
        verifiedState === "all" ? t.documents.bulkActions.unverify : t.documents.bulkActions.verify,
      onClick: onToggleVerified,
      group: GROUP.document,
      collapseRank: COLLAPSE_RANK.verify,
    });
  }
  if (selectedCount === 1 && onRename) {
    actions.push({
      key: "rename",
      icon: TextCursor,
      label: t.documents.bulkActions.rename,
      onClick: onRename,
      group: GROUP.document,
      collapseRank: COLLAPSE_RANK.rename,
    });
  }
  if (selectedCount === 1 && onComments) {
    actions.push({
      key: "comments",
      icon: MessageSquare,
      label: t.documents.bulkActions.comments,
      onClick: onComments,
      group: GROUP.document,
      collapseRank: COLLAPSE_RANK.comments,
    });
  }

  // Context actions — what the current selection specifically allows.
  if (canViewDrawing && onViewDrawing) {
    actions.push({
      key: "view-drawing",
      icon: Eye,
      label: t.documents.bulkActions.viewDrawing,
      onClick: onViewDrawing,
      group: GROUP.context,
      collapseRank: COLLAPSE_RANK.viewDrawing,
    });
  }
  // Single-file actions (Add new version, and Rename further down). Only
  // present when exactly one document is selected — mirrors
  // the row 3-dots menu.
  if (selectedCount === 1 && onAddNewVersion) {
    actions.push({
      key: "addNewVersion",
      icon: opensVersioning ? Layers : FilePlus2,
      label: opensVersioning
        ? t.documents.bulkActions.versioning
        : t.documents.bulkActions.addNewVersion,
      onClick: onAddNewVersion,
      group: GROUP.context,
      collapseRank: COLLAPSE_RANK.addNewVersion,
    });
  }
  if (canCompareDrawings && onCompareDrawings) {
    actions.push({
      key: "compare-drawings",
      icon: GitCompare,
      label: t.documents.bulkActions.compareDrawings,
      onClick: onCompareDrawings,
      group: GROUP.context,
      collapseRank: COLLAPSE_RANK.compareDrawings,
    });
  }
  if (canCompare && onCompare) {
    actions.push({
      key: "compare",
      icon: GitCompare,
      label: t.documents.bulkActions.comparePdfs,
      onClick: onCompare,
      group: GROUP.context,
      collapseRank: COLLAPSE_RANK.compare,
    });
  }
  if (canCompareModels && onDiffOverview) {
    actions.push({
      key: "diff-overview",
      icon: GitCompare,
      label: t.documents.bulkActions.diffOverview,
      onClick: onDiffOverview,
      group: GROUP.context,
      collapseRank: COLLAPSE_RANK.diffOverview,
    });
  }

  if (hostAction) {
    actions.push({
      key: "host-action",
      icon: hostAction.icon,
      label: hostAction.label,
      onClick: hostAction.onClick,
      group: GROUP.askFranklin,
      collapseRank: COLLAPSE_RANK.hostAction,
    });
  }

  if (onAskFranklin) {
    actions.push({
      key: "franklin",
      icon: MessageSquare,
      label: t.documents.bulkActions.askFranklin,
      onClick: onAskFranklin,
      group: GROUP.askFranklin,
      collapseRank: COLLAPSE_RANK.askFranklin,
    });
  }

  if (onDelete) {
    actions.push({
      key: "delete",
      icon: Trash2,
      label: t.documents.bulkActions.moveToTrash,
      onClick: () => setShowDeleteConfirm(true),
      group: GROUP.delete,
      collapseRank: COLLAPSE_RANK.delete,
    });
  }

  // Signature of the visible action set. The collapse step is measured for one
  // specific set; when the set changes (selection crossing 1 adds/removes the
  // single-file actions, compare availability, custom actions) the step
  // describes a stale layout, so it resets and re-collapses from the top.
  const actionSetKey = actions.map((a) => a.key).join(",") + (customActions ? "|custom" : "");

  // Reset to the widest layout whenever the action set changes, so the
  // measuring effect below re-collapses from the top. Runs before paint, so the
  // reset never flashes on screen. Still needed even though the measuring
  // effect is keyed on actionSetKey too: nextStep only ever grows the step, so
  // without this reset a narrower action set would stay over-collapsed.
  useLayoutEffect(() => {
    setStep(0);
  }, [actionSetKey]);

  // Collapse one step whenever the rendered bar overruns the viewport. Runs
  // before paint (useLayoutEffect) and is keyed on `step`, so the whole collapse
  // sequence completes in one commit — an overrunning layout never flashes. The
  // step only ever grows here; expanding back is triggered by the viewport
  // growing (below) or the action set changing (above), which keeps the loop
  // from oscillating between two layouts.
  useLayoutEffect(() => {
    const evaluate = () => {
      const width = barRef.current?.offsetWidth ?? 0;
      setStep((current) => nextStep(current, width, window.innerWidth, actions.length));
    };
    const onResize = () => {
      // More room than last time: start over from the widest layout and let the
      // measurement collapse it again to whatever now fits.
      if (window.innerWidth > viewportRef.current) setStep(0);
      viewportRef.current = window.innerWidth;
      evaluate();
    };
    evaluate();
    window.addEventListener("resize", onResize);
    let observer: ResizeObserver | undefined;
    if (barRef.current && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(evaluate);
      observer.observe(barRef.current);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      observer?.disconnect();
    };
    // actionSetKey (not just actions.length) is a dep: a same-count content
    // swap can leave `step` unchanged (the reset above bails on the identical
    // value), yet the new content still needs a re-measure before paint.
  }, [step, actionSetKey, actions.length]);

  if (selectedCount === 0) return null;

  // The badge shows documents only; folders are tracked separately and must not
  // inflate the document count. Callers without folders omit documentCount.
  const documentBadgeCount = documentCount ?? selectedCount;
  const selectedCountLabel = selectionLabel ?? t.documents.bulkActions.selectedCount;

  // While the selection is still cascading, every action is disabled — acting
  // on a half-resolved selection would operate on the wrong set of documents.
  const actionsDisabled = isSelectionPending;

  // Icons are the last resort — only once even three labelled actions no longer
  // fit beside the count (see isCompactStep).
  const compact = isCompactStep(step, actions.length);
  const hidden = hiddenKeysForStep(actions, step);
  const overflowActions = actions.filter((a) => hidden.has(a.key));

  const countBadge = (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EF4129]">
      {isSelectionPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-white" aria-label={selectedCountLabel} />
      ) : (
        <span className="text-xs font-medium text-white">{documentBadgeCount}</span>
      )}
    </div>
  );

  // Divider groups, in bar order. A group with nothing visible renders nothing —
  // and no divider — so the bar never shows a rule with empty space beside it.
  const groups: ReactNode[][] = [];
  const pushGroup = (group: number) => {
    const items = actions
      .filter((a) => a.group === group && !hidden.has(a.key))
      .map((a) => (
        <BulkActionButton
          key={a.key}
          icon={a.icon}
          label={a.label}
          onClick={a.onClick}
          disabled={actionsDisabled}
          layout={compact ? "icon" : "vertical"}
        />
      ));
    if (items.length > 0) groups.push(items);
  };

  pushGroup(GROUP.document);
  pushGroup(GROUP.context);
  pushGroup(GROUP.askFranklin);
  // Domain-specific actions slotted in by the caller (e.g. issues page bulk
  // pickers). Never collapses into "…" — it's an opaque node, not a menu item.
  if (customActions) {
    groups.push([<Fragment key="custom-actions">{customActions({ compact })}</Fragment>]);
  }
  // "…" sits just left of Delete, so Delete stays the last thing on the bar.
  if (overflowActions.length > 0) {
    groups.push([
      // modal={false}: a modal menu locks body pointer-events; a dialog opened
      // from an item (Delete → confirm, Share, Rename, …) races that lock as
      // the menu closes and can leave the page unclickable.
      <DropdownMenu key="overflow" modal={false}>
        <DropdownMenuTrigger asChild>
          <BulkActionButton
            icon={MoreHorizontal}
            label={t.documents.bulkActions.more}
            disabled={actionsDisabled}
            layout={compact ? "icon" : "vertical"}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="end" sideOffset={8}>
          {overflowActions.map((a, index) => {
            const Icon = a.icon;
            return (
              <Fragment key={a.key}>
                {/* Delete keeps its separation from the rest in the menu too. */}
                {a.key === "delete" && index > 0 && <DropdownMenuSeparator />}
                <DropdownMenuItem
                  disabled={actionsDisabled}
                  // Deferred: most handlers open a dialog, and mounting one
                  // synchronously from onSelect collides with the menu's own
                  // close/focus-restore (focus stolen from the dialog).
                  onSelect={() => runAfterMenuCloses(a.onClick)}
                  className="gap-2"
                >
                  <Icon className="h-4 w-4" />
                  <span>{a.label}</span>
                </DropdownMenuItem>
              </Fragment>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>,
    ]);
  }
  pushGroup(GROUP.delete);

  return (
    <>
      <div
        className={cn(
          "fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center rounded-[10px] bg-neutral-800 shadow-[0px_20px_25px_0px_rgba(0,0,0,0.3),0px_10px_10px_0px_rgba(0,0,0,0.2)]",
          compact ? "px-2" : "justify-between px-3"
        )}
        ref={barRef}
      >
        {/* Left section: badge + count + clear. Compact drops the count text to
            save width but keeps it for screen readers. */}
        <div className={cn("flex items-center", compact ? "gap-1" : "gap-3")}>
          <div className="flex items-center gap-2">
            {countBadge}
            {compact ? (
              <span className="sr-only">{selectedCountLabel}</span>
            ) : (
              <span className="whitespace-nowrap text-xs font-medium text-white">
                {selectedCountLabel}
              </span>
            )}
          </div>
          <BulkActionButton
            icon={X}
            label={t.documents.bulkActions.clear}
            onClick={onClearSelection}
            disabled={actionsDisabled}
            layout={compact ? "icon" : "horizontal"}
          />
        </div>

        {/* Right section: action groups, each behind its own divider */}
        <div className="flex items-center">
          {groups.map((group, groupIndex) => (
            <div key={groupIndex} className="flex items-center">
              <Divider />
              <div className="flex items-center">{group}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete confirmation dialog */}
      {onDelete && (
        <ConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title={t.documents.bulkActions.confirmTrashTitle}
          description={t.documents.bulkActions.confirmTrashDescription.replace(
            "{count}",
            String(selectedCount)
          )}
          onConfirm={() => {
            setShowDeleteConfirm(false);
            onDelete();
          }}
          confirmLabel={t.documents.bulkActions.moveToTrash}
          variant="destructive"
        />
      )}
    </>
  );
});
